import { NotebookEngineProfile } from '../gen/ai/h2o/engine/v1/notebook_engine_profile_pb';
import {
  CreateNotebookEngineProfileRequest,
  CreateNotebookEngineProfileResponse,
  DeleteNotebookEngineProfileRequest,
  DeleteNotebookEngineProfileResponse,
  GetNotebookEngineProfileRequest,
  GetNotebookEngineProfileResponse,
  ListNotebookEngineProfilesRequest,
  ListNotebookEngineProfilesResponse,
  NotebookEngineProfileService_CreateNotebookEngineProfile,
  NotebookEngineProfileService_DeleteNotebookEngineProfile,
  NotebookEngineProfileService_GetNotebookEngineProfile,
  NotebookEngineProfileService_ListNotebookEngineProfiles,
  NotebookEngineProfileService_UpdateNotebookEngineProfile,
  UpdateNotebookEngineProfileRequest,
  UpdateNotebookEngineProfileResponse,
} from '../gen/ai/h2o/engine/v1/notebook_engine_profile_service_pb';
import { BigIntString } from '../gen/runtime';
import {
  engineProfileBaseFields,
  engineProfileFieldsConfig,
  engineProfilesColumns,
  EntityActionType,
  EntityFieldType,
  EntityType,
} from './constants';
import { DependencyOperator } from './types';
import { Entity, EntityAction, EntityField } from './types';

const entityType = EntityType.NotebookEngineProfile;

export const actions = {
  [EntityActionType.Create]: {
    name: 'CreateNotebookEngineProfile',
    rpc: NotebookEngineProfileService_CreateNotebookEngineProfile,
    type: EntityActionType.Create,
    responseKey: 'notebookEngineProfile',
    requestNameKey: 'notebookEngineProfileId',
    requestPayloadKey: 'notebookEngineProfile',
    requestNameKeyPrefix: 'notebookEngineProfile/',
    entityType,
  } as EntityAction<NotebookEngineProfile, CreateNotebookEngineProfileRequest, CreateNotebookEngineProfileResponse>,
  [EntityActionType.Get]: {
    name: 'GetNotebookEngineProfile',
    rpc: NotebookEngineProfileService_GetNotebookEngineProfile,
    type: EntityActionType.Get,
    requestNameKey: 'name' as unknown,
    responseKey: 'notebookEngineProfile',
    entityType,
  } as EntityAction<NotebookEngineProfile, GetNotebookEngineProfileRequest, GetNotebookEngineProfileResponse>,
  [EntityActionType.List]: {
    name: 'ListNotebookEngineProfile',
    rpc: NotebookEngineProfileService_ListNotebookEngineProfiles,
    type: EntityActionType.List,
    responseKey: 'notebookEngineProfiles',
    entityType,
  } as EntityAction<NotebookEngineProfile, ListNotebookEngineProfilesRequest, ListNotebookEngineProfilesResponse>,
  [EntityActionType.Update]: {
    name: 'UpdateNotebookEngineProfile',
    rpc: NotebookEngineProfileService_UpdateNotebookEngineProfile,
    type: EntityActionType.Update,
    responseKey: 'notebookEngineProfile',
    requestNameKey: 'name' as unknown,
    requestPayloadKey: 'notebookEngineProfile',
    entityType,
  } as EntityAction<NotebookEngineProfile, UpdateNotebookEngineProfileRequest, UpdateNotebookEngineProfileResponse>,
  [EntityActionType.Delete]: {
    name: 'DeleteNotebookEngineProfile',
    rpc: NotebookEngineProfileService_DeleteNotebookEngineProfile,
    type: EntityActionType.Delete,
    entityType,
  } as EntityAction<NotebookEngineProfile, DeleteNotebookEngineProfileRequest, DeleteNotebookEngineProfileResponse>,
};

const engineProfileFields = engineProfileFieldsConfig<NotebookEngineProfile>('Notebook');

export const fields: EntityField<NotebookEngineProfile>[] = [
  ...engineProfileBaseFields('Notebook'),
  engineProfileFields.storageBytesConstraint,
  engineProfileFields.maxIdleDurationConstraint,
  engineProfileFields.maxRunningDurationConstraint,
  engineProfileFields.maxRunningEngines,
  engineProfileFields.yamlPodTemplateSpec,
  engineProfileFields.yamlGpuTolerations,
  engineProfileFields.gpuResourceName,
  engineProfileFields.storageClassName,
  {
    name: 'syncGitRepositoryEnabled',
    type: EntityFieldType.Boolean,
    label: 'Sync Git Repository Enabled',
    description: `Enables syncing of the git repository when NotebookEngine is created`,
  },
  {
    name: 'gitRepository',
    type: EntityFieldType.Text,
    label: 'Git Repository',
    description: `The git repository to sync`,
    dependsOn: {
      field: 'syncGitRepositoryEnabled',
      value: true,
      operator: DependencyOperator.Equals,
    },
  },
  {
    name: 'gitRef',
    type: EntityFieldType.Text,
    label: 'Git Ref',
    description: `
      The git revision (branch, tag, or hash) to check out.
      If not specified, this defaults to "HEAD" (of the upstream repo's default branch)
    `,
    dependsOn: {
      field: 'syncGitRepositoryEnabled',
      value: true,
      operator: DependencyOperator.Equals,
    },
  },
  {
    name: 'gitDirectoryName',
    type: EntityFieldType.Text,
    label: 'Git Directory Name',
    description: `The name of the directory in the user's home folder, where the Git repo is synced into.`,
    inputDefaultValue: 'Example',
    dependsOn: {
      field: 'syncGitRepositoryEnabled',
      value: true,
      operator: DependencyOperator.Equals,
    },
  },
];

export const notebookEngineProfileEntity: Entity<NotebookEngineProfile> = {
  type: EntityType.NotebookEngineProfile,
  name: 'notebookEngineProfiles',
  createButtonLabel: 'Add Notebook Engine Profile',
  displayName: 'Notebook Engine Profile',
  displayNameKey: 'displayName',
  actions,
  columns: engineProfilesColumns,
  fields,
  requestNameKeyPrefix: 'workspaces/global/notebookEngineProfiles/',
  emptyModel: {
    name: 'new-notebook-engine-profile-id',
    displayName: 'New Notebook Engine Profile',
    priority: 100,
    enabled: true,
    assignedOidcRolesEnabled: false,
    assignedOidcRoles: [],
    maxRunningEngines: undefined,
    cpuConstraint: {
      min: '1' as BigIntString,
      default: '2' as BigIntString,
    },
    gpuConstraint: {
      min: '0' as BigIntString,
      default: '0' as BigIntString,
    },
    memoryBytesConstraint: {
      min: '4294967296' as BigIntString,
      default: '8589934592' as BigIntString,
    },
    storageBytesConstraint: {
      min: '4294967296' as BigIntString,
      default: '8589934592' as BigIntString,
    },
    maxIdleDurationConstraint: {
      min: '0s' as BigIntString,
      default: '3600s' as BigIntString,
    },
    maxRunningDurationConstraint: {
      min: '0s' as BigIntString,
      default: '432000s' as BigIntString,
    },
    yamlPodTemplateSpec: '',
    yamlGpuTolerations: '',
    syncGitRepositoryEnabled: false,
    gitRepository: '',
    gitRef: '',
    gitDirectoryName: 'Example',
  },
  canBeReordered: false,
};
